import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaArrowDown, FaHistory, FaTimes } from "react-icons/fa";
import { formatDisplayDate, getStatusBadgeClass } from "./commissionsConstants";

function toSortTime(value) {
  const raw = String(value || "").replace(/(\.\d{3})\d+(Z)?$/i, "$1$2");
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Date(`${raw}T00:00:00`).getTime() || 0;
  }
  const time = Date.parse(raw);
  return Number.isFinite(time) ? time : 0;
}

function normalizeHistoryInput(statusHistory) {
  if (Array.isArray(statusHistory)) return statusHistory;
  if (statusHistory && typeof statusHistory === "object") {
    return Object.values(statusHistory);
  }
  return [];
}

export function normalizeCommissionStatusHistory(statusHistory = []) {
  return normalizeHistoryInput(statusHistory)
    .map((item) => {
      const title = String(
        item?.status_title ?? item?.statusTitle ?? item?.title ?? "",
      ).trim();
      if (!title) return null;

      return {
        id: item?.id,
        status_title: title,
        status_change_at:
          item?.status_change_at ??
          item?.status_change_date ??
          item?.created_at ??
          null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const idA = Number(a.id || 0);
      const idB = Number(b.id || 0);
      if (idA !== idB) return idA - idB;
      return toSortTime(a.status_change_at) - toSortTime(b.status_change_at);
    });
}

/** Badge + modal use the same list; if API history is empty, show current status once. */
export function buildCommissionDisplayHistory(
  statusHistory = [],
  fallbackTitle = "—",
  fallbackAt = null,
) {
  const normalized = normalizeCommissionStatusHistory(statusHistory);
  if (normalized.length > 0) return normalized;

  const title = String(fallbackTitle || "").trim();
  if (!title || title === "—") return [];

  return [
    {
      id: "current",
      status_title: title,
      status_change_at: fallbackAt ?? null,
    },
  ];
}

function preventBackdropScroll(event) {
  event.preventDefault();
}

export function StatusHistoryModal({
  open,
  onClose,
  history,
  stackTop = false,
  loading = false,
}) {
  const scrollYRef = useRef(0);

  useLayoutEffect(() => {
    if (!open) return undefined;

    scrollYRef.current = window.scrollY;
    window.scrollTo(0, scrollYRef.current);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.scrollTo(0, scrollYRef.current);
    };
  }, [open, onClose]);

  if (!open) return null;

  const backdropClass = stackTop
    ? "cms-app-modal-backdrop cms-app-modal-backdrop--stack-top bg-black/40"
    : "cms-app-modal-backdrop bg-black/40";
  const shellClass = stackTop
    ? "cms-app-modal cms-app-modal--centered cms-app-modal--stack-top flex items-center justify-center p-3 sm:p-4"
    : "cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4";

  return createPortal(
    <>
      <div
        className={backdropClass}
        onClick={onClose}
        onWheel={preventBackdropScroll}
        onTouchMove={preventBackdropScroll}
        aria-hidden="true"
      />
      <div
        className={shellClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby="commission-status-history-title"
        onClick={onClose}
        onWheel={preventBackdropScroll}
        onTouchMove={preventBackdropScroll}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
          style={{ maxWidth: 420 }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 bg-gradient-to-r from-[#F8FAFC] to-white px-3.5 py-2">
            <div className="min-w-0">
              <p
                id="commission-status-history-title"
                className="text-[12px] font-bold uppercase tracking-wide text-gray-600"
              >
                Status History
                <span className="ml-2 font-semibold normal-case tracking-normal text-gray-400">
                  ({history.length} {history.length === 1 ? "entry" : "entries"})
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close status history"
              style={{ border: "none", outline: "none", boxShadow: "none" }}
            >
              <FaTimes size={13} />
            </button>
          </div>

          <div className="cms-scrollbar-hidden min-h-0 flex-1 overflow-y-auto p-3.5 sm:p-4">
            {loading ? (
              <p className="px-2 py-6 text-center text-[12px] text-gray-400">
                Loading status history…
              </p>
            ) : history.length === 0 ? (
              <p className="px-2 py-6 text-center text-[12px] text-gray-400">
                No status history
              </p>
            ) : (
              <ol className="cms-commission-status-history m-0 list-none p-0">
                {history.map((item, index) => {
                  const badgeClass = getStatusBadgeClass(item.status_title);
                  const isStart = index === 0;
                  const isCurrent = index === history.length - 1;
                  const nextTitle = history[index + 1]?.status_title;
                  const dateLabel = formatDisplayDate(item.status_change_at) || "—";

                  return (
                    <li
                      key={item.id || `${item.status_title}-${item.status_change_at}-${index}`}
                      className="cms-commission-status-history__item"
                    >
                      <div
                        className="cms-commission-status-history__rail"
                        aria-hidden={!isCurrent}
                      >
                        <span
                          className={`cms-commission-status-history__dot ${
                            isCurrent
                              ? "cms-commission-status-history__dot--current"
                              : ""
                          }`}
                        />
                        {!isCurrent ? (
                          <div className="cms-commission-status-history__connector">
                            <span className="cms-commission-status-history__line" />
                            <FaArrowDown
                              className="cms-commission-status-history__arrow"
                              size={10}
                            />
                            <span className="cms-commission-status-history__line" />
                          </div>
                        ) : null}
                      </div>
                      <div className="cms-commission-status-history__body">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex max-w-full truncate rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight ${badgeClass}`}
                          >
                            {item.status_title}
                          </span>
                          {isStart ? (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              Start
                            </span>
                          ) : null}
                          {isCurrent ? (
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                              Current
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-[11px] tabular-nums text-gray-400">
                          {dateLabel}
                        </p>
                        {!isCurrent && nextTitle ? (
                          <p className="cms-commission-status-history__transition">
                            to {nextTitle}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function CommissionsStatusHistory({
  statusHistory = [],
  fallbackTitle = "—",
  fallbackAt = null,
  stackTop = false,
  onBeforeOpen,
  /** "inline" (default) | "corner" — corner = small blue circle on top-right of the button */
  countBadgeVariant = "inline",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(null);

  useEffect(() => {
    setLoadedHistory(null);
  }, [statusHistory]);

  const history = useMemo(() => {
    const raw = loadedHistory !== null ? loadedHistory : statusHistory;
    return buildCommissionDisplayHistory(raw, fallbackTitle, fallbackAt);
  }, [loadedHistory, statusHistory, fallbackTitle, fallbackAt]);

  const latestTitle =
    history[history.length - 1]?.status_title || fallbackTitle;
  const latestBadge = getStatusBadgeClass(latestTitle);

  const statusEntryCount = history.length;

  const handleOpen = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);

    if (!onBeforeOpen) return;

    setLoading(true);
    try {
      const result = await onBeforeOpen();
      if (result) {
        setLoadedHistory(result);
      }
    } catch {
      /* keep row / prop history */
    } finally {
      setLoading(false);
    }
  };

  const countTitle = `${statusEntryCount} status ${
    statusEntryCount === 1 ? "change" : "changes"
  }`;

  return (
    <>
      <button
        type="button"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={handleOpen}
        className={`relative inline-flex max-w-[160px] items-center gap-1 rounded-md border px-2 py-1 text-[12px] font-semibold transition hover:brightness-95 ${latestBadge} ${
          countBadgeVariant === "corner" && statusEntryCount > 0 ? "pr-2" : ""
        }`}
        style={{ outline: "none", boxShadow: "none" }}
        aria-label={`View status history for ${latestTitle}`}
      >
        <FaHistory size={11} className="shrink-0 opacity-70" />
        <span className="truncate">View Status</span>
        {statusEntryCount > 0 && countBadgeVariant === "inline" ? (
          <span
            className="ml-0.5 shrink-0 rounded-full bg-black/12 px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none"
            title={countTitle}
          >
            {statusEntryCount}
          </span>
        ) : null}
        {statusEntryCount > 0 && countBadgeVariant === "corner" ? (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#007BFF] px-1 text-[10px] font-bold tabular-nums leading-none text-white shadow-sm ring-2 ring-white"
            title={countTitle}
            aria-hidden
          >
            {statusEntryCount > 99 ? "99+" : statusEntryCount}
          </span>
        ) : null}
      </button>

      <StatusHistoryModal
        open={open}
        onClose={() => setOpen(false)}
        history={history}
        stackTop={stackTop}
        loading={loading}
      />
    </>
  );
}

export default CommissionsStatusHistory;
